import React from 'react';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItemButton, ListItemIcon, ListItemText, IconButton, useTheme, Tooltip, Collapse } from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlagIcon from '@mui/icons-material/Flag';
import MenuIcon from '@mui/icons-material/Menu';
import FolderIcon from '@mui/icons-material/Folder';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Build as BuildIcon } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import SportsIcon from '@mui/icons-material/Sports';
import { logout } from '../../services/auth';
import { ThemeContext } from '../../App';

const drawerWidth = 240;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [manualActionsOpen, setManualActionsOpen] = React.useState(false);
  const theme = useTheme();
  const themeContext = React.useContext(ThemeContext);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleThemeToggle = () => {
    themeContext.toggleColorMode();
  };

  const handleManualActionsClick = () => {
    setManualActionsOpen(!manualActionsOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Tournaments', icon: <EmojiEventsIcon />, path: '/tournaments?status=ONGOING' },
    { text: 'Matches', icon: <SportsCricketIcon />, path: '/matches?status=Not%20Started' },
    { text: 'Sport Events', icon: <SportsIcon />, path: '/sport-events' },
    { text: 'Players', icon: <PeopleIcon />, path: '/players' },
    { text: 'Match Players', icon: <PersonIcon />, path: '/match-players' },
    { text: 'Match Player Groups', icon: <SettingsIcon />, path: '/match-player-group-configs' },
    { text: 'Squad Players', icon: <GroupIcon />, path: '/squad-players' },
    { text: 'Teams', icon: <FlagIcon />, path: '/teams' },
    { text: 'Grounds', icon: <LocationOnIcon />, path: '/grounds' },
    { text: 'Menu Management', icon: <MenuIcon />, path: '/menu-management' },
    { text: 'Media Management', icon: <FolderIcon />, path: '/media' },
    { text: 'User Management', icon: <SupervisorAccountIcon />, path: '/users' },
    { text: 'Connect', icon: <NotificationsIcon />, path: '/connect' },
    { text: 'Expo Updates', icon: <PhoneIphoneIcon />, path: '/expo-updates' },
    {
      text: 'Manual Actions',
      icon: <BuildIcon />,
      path: '/manual-actions',
      subItems: [
        { text: 'All Actions', path: '/manual-actions' },
        { text: 'Pending Actions', path: '/manual-actions?done=false&cancelled=false' },
        { text: 'Done Actions', path: '/manual-actions?done=true' },
        { text: 'Cancelled Actions', path: '/manual-actions?cancelled=true' },
      ],
    },
  ];

  const renderMenuItem = (item: any) => {
    if (item.subItems) {
      return (
        <React.Fragment key={item.text}>
          <ListItemButton onClick={handleManualActionsClick}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {manualActionsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={manualActionsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems.map((subItem: any) => (
                <ListItemButton
                  key={subItem.text}
                  sx={{ pl: 4 }}
                  onClick={() => navigate(subItem.path)}
                  selected={location.pathname + location.search === subItem.path}
                >
                  <ListItemText primary={subItem.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton
        key={item.text}
        onClick={() => navigate(item.path)}
        selected={location.pathname + location.search === item.path}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    );
  };

  const drawer = (
    <div>
      <Toolbar />
      <List>
        {menuItems.map(renderMenuItem)}
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon><ExitToAppIcon /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
        <ListItemButton onClick={handleThemeToggle}>
          <ListItemIcon>
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </ListItemIcon>
          <ListItemText primary={theme.palette.mode === 'dark' ? "Light Mode" : "Dark Mode"} />
        </ListItemButton>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Ai11 Admin
          </Typography>
          
          {/* Theme toggle button */}
          <Tooltip title={theme.palette.mode === 'dark' ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton color="inherit" onClick={handleThemeToggle} sx={{ ml: 1 }}>
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          
          {/* Logout button */}
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>
              <ExitToAppIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;